import { AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';

@Component({
  selector: 'bst-area-chart',
  templateUrl: './area-chart.component.html',
  styleUrls: ['./area-chart.component.scss'],
  standalone: false
})
export class AreaChartComponent implements AfterViewInit, OnChanges {
  /** Parallel arrays — months[i] labels the x-axis tick for values[i]. */
  @Input() months: string[] = [];
  @Input() values: number[] = [];

  @ViewChild('host', { static: true }) hostRef!: ElementRef<HTMLDivElement>;

  ngAfterViewInit(): void {
    this.render();
  }

  ngOnChanges(_: SimpleChanges): void {
    if (this.hostRef) {
      this.render();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.render();
  }

  private render(): void {
    const host = this.hostRef.nativeElement;
    const width = host.clientWidth || 600;
    const height = 160;
    const margin = { top: 12, right: 12, bottom: 24, left: 36 };

    d3.select(host).select('svg').remove();

    if (!this.values?.length) return;

    const svg = d3.select(host)
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    const xScale = d3.scaleLinear()
      .domain([0, Math.max(this.values.length - 1, 1)])
      .range([margin.left, width - margin.right]);

    const yMax = Math.max(...this.values, 1);
    const yScale = d3.scaleLinear()
      .domain([0, yMax])
      .nice()
      .range([height - margin.bottom, margin.top]);

    // Y gridlines
    svg.append('g')
      .attr('class', 'gridlines')
      .selectAll('line')
      .data(yScale.ticks(4))
      .enter()
      .append('line')
      .attr('x1', margin.left)
      .attr('x2', width - margin.right)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .attr('stroke', '#edf1f7')
      .attr('stroke-width', 1);

    // Y axis labels
    svg.append('g')
      .attr('class', 'y-axis')
      .selectAll('text')
      .data(yScale.ticks(4))
      .enter()
      .append('text')
      .attr('x', margin.left - 6)
      .attr('y', d => yScale(d))
      .attr('dy', '0.32em')
      .attr('text-anchor', 'end')
      .attr('fill', '#6B7A89')
      .style('font-size', '10px')
      .text(d => String(d));

    // X axis labels (subset to avoid overlap)
    const xTickStep = Math.max(1, Math.floor(this.months.length / 6));
    svg.append('g')
      .attr('class', 'x-axis')
      .selectAll('text')
      .data(this.months.filter((_m, i) => i % xTickStep === 0 || i === this.months.length - 1))
      .enter()
      .append('text')
      .attr('x', m => xScale(this.months.indexOf(m)))
      .attr('y', height - margin.bottom + 14)
      .attr('text-anchor', 'middle')
      .attr('fill', '#6B7A89')
      .style('font-size', '10px')
      .text(m => m);

    // Area + line
    const area = d3.area<number>()
      .x((_d, i) => xScale(i))
      .y0(yScale(0))
      .y1(d => yScale(d))
      .curve(d3.curveMonotoneX);

    const line = d3.line<number>()
      .x((_d, i) => xScale(i))
      .y(d => yScale(d))
      .curve(d3.curveMonotoneX);

    svg.append('path')
      .attr('d', area(this.values))
      .attr('fill', 'rgba(51, 102, 255, 0.18)');

    svg.append('path')
      .attr('d', line(this.values))
      .attr('fill', 'none')
      .attr('stroke', '#1863DC')
      .attr('stroke-width', 2);
  }
}
